import { readableTextColor } from './ctaTheme';

export const scanPageFontValues = ['serif', 'sora', 'space-grotesk'] as const;
export const scanPageCrosshairValues = ['corners', 'circle', 'focus'] as const;
export const scanPageButtonStyleValues = ['pill', 'rounded', 'square'] as const;

export type ScanPageFont = (typeof scanPageFontValues)[number];
export type ScanPageCrosshair = (typeof scanPageCrosshairValues)[number];
export type ScanPageButtonStyle = (typeof scanPageButtonStyleValues)[number];

export type ScanPageConfig = {
  version: 1;
  headline: string;
  instructions: string;
  buttonLabel: string;
  font: ScanPageFont;
  crosshair: ScanPageCrosshair;
  accentColor: string;
  textColor: string;
  buttonColor: string;
  buttonStyle: ScanPageButtonStyle;
};

export const DEFAULT_SCAN_PAGE_CONFIG: ScanPageConfig = {
  version: 1,
  headline: 'Bring this moment to life',
  instructions: 'Point your camera at the image and hold steady for a moment.',
  buttonLabel: 'Tap to begin',
  font: 'serif',
  crosshair: 'corners',
  accentColor: '#F3C77F',
  textColor: '#FFF8EC',
  buttonColor: '#F3C77F',
  buttonStyle: 'pill'
};

const hexPattern = /^#[0-9a-f]{6}$/i;

const normalizeColor = (value: unknown, fallback: string) =>
  typeof value === 'string' && hexPattern.test(value.trim()) ? value.trim().toUpperCase() : fallback;

const normalizeCopy = (value: unknown, fallback: string, maxLength: number) => {
  if (typeof value !== 'string') return fallback;
  return value.trim().slice(0, maxLength) || fallback;
};

export const normalizeScanPageConfig = (
  value: unknown,
  fallback: ScanPageConfig = DEFAULT_SCAN_PAGE_CONFIG
): ScanPageConfig => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { ...fallback };
  const candidate = value as Record<string, unknown>;
  const font = scanPageFontValues.includes(candidate.font as ScanPageFont)
    ? candidate.font as ScanPageFont
    : fallback.font;
  const crosshair = scanPageCrosshairValues.includes(candidate.crosshair as ScanPageCrosshair)
    ? candidate.crosshair as ScanPageCrosshair
    : fallback.crosshair;
  const buttonStyle = scanPageButtonStyleValues.includes(candidate.buttonStyle as ScanPageButtonStyle)
    ? candidate.buttonStyle as ScanPageButtonStyle
    : fallback.buttonStyle;

  return {
    version: 1,
    headline: normalizeCopy(candidate.headline, fallback.headline, 72),
    instructions: normalizeCopy(candidate.instructions, fallback.instructions, 160),
    buttonLabel: normalizeCopy(candidate.buttonLabel, fallback.buttonLabel, 36),
    font,
    crosshair,
    accentColor: normalizeColor(candidate.accentColor, fallback.accentColor),
    textColor: normalizeColor(candidate.textColor, fallback.textColor),
    buttonColor: normalizeColor(candidate.buttonColor, fallback.buttonColor),
    buttonStyle
  };
};

export const isScanPageConfigured = (value: unknown): value is ScanPageConfig => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate.version === 1 &&
    typeof candidate.headline === 'string' &&
    candidate.headline.trim().length > 0 &&
    candidate.headline.trim().length <= 72 &&
    typeof candidate.instructions === 'string' &&
    candidate.instructions.trim().length > 0 &&
    candidate.instructions.trim().length <= 160 &&
    typeof candidate.buttonLabel === 'string' &&
    candidate.buttonLabel.trim().length > 0 &&
    candidate.buttonLabel.trim().length <= 36 &&
    scanPageFontValues.includes(candidate.font as ScanPageFont) &&
    scanPageCrosshairValues.includes(candidate.crosshair as ScanPageCrosshair) &&
    scanPageButtonStyleValues.includes(candidate.buttonStyle as ScanPageButtonStyle) &&
    typeof candidate.accentColor === 'string' &&
    hexPattern.test(candidate.accentColor.trim()) &&
    typeof candidate.textColor === 'string' &&
    hexPattern.test(candidate.textColor.trim()) &&
    typeof candidate.buttonColor === 'string' &&
    hexPattern.test(candidate.buttonColor.trim())
  );
};

const fontFamilies: Record<ScanPageFont, string> = {
  serif: "Georgia, 'Times New Roman', serif",
  sora: "'Sora', system-ui, sans-serif",
  'space-grotesk': "'Space Grotesk', system-ui, sans-serif"
};

const buttonRadii: Record<ScanPageButtonStyle, string> = {
  pill: '999px',
  rounded: '1rem',
  square: '0.45rem'
};

export const scanPageConfigCssVariables = (value: unknown): Record<string, string> => {
  const config = normalizeScanPageConfig(value);
  return {
    '--scan-font-family': fontFamilies[config.font],
    '--scan-accent-color': config.accentColor,
    '--scan-text-color': config.textColor,
    '--scan-button-color': config.buttonColor,
    '--scan-button-text': readableTextColor(config.buttonColor),
    '--scan-button-radius': buttonRadii[config.buttonStyle]
  };
};
